"use client";

import { asset } from '@/lib/assets'
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import styles from "./LoginModal.module.css";
import { API_BASE_URL, API_ENDPOINTS } from "@/lib/config";
import { setAuthToken } from "@/lib/auth";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { useGithubAuth } from "@/hooks/useGithubAuth";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import {
  loginAttempt,
  loginFailure,
  loginSuccess,
  signUpFailure,
  signUpStart,
  signUpSuccessful,
  type LandingLoginEntry,
} from "@/lib/ga";
import { trackLogin, trackSignup } from "@/lib/analytics";
import { resolvePostLoginRedirectOrDashboardAsync } from "@/lib/postLoginRedirect";
import { getReferralCookie, clearReferralCookie } from "@/lib/referralCookie";
import { submitReferralCode } from "@/lib/referral";
import { ReferralCodeModal } from "@/components/ui/ReferralCodeModal";
import { isEnterpriseLoginEnabled } from "@/lib/enterpriseLoginFeature";
import { validateEmail } from "@/utils/validateEmail";
import { applyOtpPaste } from "@/lib/otpInput";
import { otpDeadlineMs, otpRemainingSeconds } from "@/lib/otpCountdown";

interface SignInResponse {
  message: string;
  userData: {
    email: string;
    name: string;
    apiToken: string;
    profileImageUrl: string;
    user_id?: string;
  };
  skillAssessmentRedirect: boolean;
  shouldPromptForReferral?: boolean;
  isNewUser?: boolean;
}

interface GoogleLoginResponse {
  message: string;
  userData: {
    email: string;
    name: string;
    apiToken: string;
    profileImageUrl: string;
    user_id?: string;
  };
  skillAssessmentRedirect: boolean;
  shouldPromptForReferral: boolean;
  isNewUser?: boolean;
}

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  loginEntry?: LandingLoginEntry;
  required?: boolean;
}

const RECRUITER_ENTERPRISE_ONLY_ERROR =
  "Recruiter accounts can only sign in through enterprise access.";
const RECRUITER_ENTERPRISE_ONLY_UI_MESSAGE_ENABLED =
  "This email is linked to an enterprise. Please sign in via the enterprise page (work email, Google or code).";
const RECRUITER_ENTERPRISE_ONLY_UI_MESSAGE_DISABLED =
  "This email is linked to an enterprise. Employer sign-in is not enabled on this site.";

function payloadIsNewUser(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const record = data as Record<string, unknown>;
  return record.isNewUser === true || record.is_new_user === true;
}

export function LoginModal({
  open,
  onClose,
  loginEntry = "unknown",
  required = false,
}: LoginModalProps) {
  const mapRecruiterOnlyError = (message: string): string =>
    message.includes(RECRUITER_ENTERPRISE_ONLY_ERROR)
      ? isEnterpriseLoginEnabled()
        ? RECRUITER_ENTERPRISE_ONLY_UI_MESSAGE_ENABLED
        : RECRUITER_ENTERPRISE_ONLY_UI_MESSAGE_DISABLED
      : message;

  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [otpDeadlineMsState, setOtpDeadlineMsState] = useState<number | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [pendingApiToken, setPendingApiToken] = useState<string | null>(null);
  const [pendingRedirect, setPendingRedirect] = useState<{
    skillAssessment: boolean;
  } | null>(null);
  const [signupStarted, setSignupStarted] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    handleGoogleLogin: triggerGoogleLogin,
    isLoading: isGoogleLoading,
    error: googleError,
  } = useGoogleAuth();

  const {
    handleGithubLogin: triggerGithubLogin,
    isLoading: isGithubLoading,
  } = useGithubAuth();

  useEffect(() => {
    if (otpDeadlineMsState == null) {
      setResendCountdown(0);
      return;
    }
    const tick = () => setResendCountdown(otpRemainingSeconds(otpDeadlineMsState));
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [otpDeadlineMsState]);

  useBodyScrollLock(open);

  // Reset form state when modal opens
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpDeadlineMsState(null);
    setError("");
    setSignupStarted(false);
  }, [open]);

  const sendOtpApi = async (emailVal: string, purpose: "login" = "login") => {
    const res = await fetch(`${API_BASE_URL}/users/send-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailVal, purpose }),
    });
    const data = await res.json();
    if (!res.ok)
      throw new Error(data.detail || data.message || "Failed to send OTP");
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (!otpSent) {
        if (!email?.trim()) {
          setError("Please enter your email");
          loginFailure({
            method: "email_otp",
            login_entry: loginEntry,
            failure_step: "client_validation",
            error_message: "Please enter your email",
          });
          return;
        }
        const emailValidationError = validateEmail(email);
        if (emailValidationError) {
          setError(emailValidationError);
          loginFailure({
            method: "email_otp",
            login_entry: loginEntry,
            failure_step: "client_validation",
            error_message: emailValidationError,
          });
          return;
        }
        const emailNorm = email.trim().toLowerCase();
        loginAttempt({ method: "email_otp", login_entry: loginEntry });
        try {
          const otpData = await sendOtpApi(emailNorm, "login");
          setOtpDeadlineMsState(otpDeadlineMs(otpData?.expires_at));
          if (payloadIsNewUser(otpData)) {
            setSignupStarted(true);
            await signUpStart({
              method: "email_otp",
              login_entry: loginEntry,
            });
          }
        } catch (sendErr) {
          const raw =
            sendErr instanceof Error ? sendErr.message : "An error occurred";
          setError(mapRecruiterOnlyError(raw));
          // No sign_up_failure here: the send is what tells us the email is new,
          // so a failed send leaves us unable to distinguish signup from login.
          loginFailure({
            method: "email_otp",
            login_entry: loginEntry,
            failure_step: "send_otp",
            error_message: raw,
          });
          return;
        }
        setOtp(["", "", "", "", "", ""]);
        setOtpSent(true);
        return;
      }
      if (resendCountdown <= 0) {
        setError("This code has expired. Please request a new OTP.");
        return;
      }
      const otpValue = otp.join("");
      if (otpValue.length !== 6) {
        setError("Please enter the complete 6-digit OTP");
        loginFailure({
          method: "email_otp",
          login_entry: loginEntry,
          failure_step: "client_validation",
          error_message: "Please enter the complete 6-digit OTP",
        });
        if (signupStarted) {
          await signUpFailure({
            method: "email_otp",
            login_entry: loginEntry,
            failure_step: "client_validation",
            error_message: "Please enter the complete 6-digit OTP",
          });
        }
        return;
      }
      const otpEmailError = validateEmail(email);
      if (otpEmailError) {
        setError(otpEmailError);
        loginFailure({
          method: "email_otp",
          login_entry: loginEntry,
          failure_step: "client_validation",
          error_message: otpEmailError,
        });
        if (signupStarted) {
          await signUpFailure({
            method: "email_otp",
            login_entry: loginEntry,
            failure_step: "client_validation",
            error_message: otpEmailError,
          });
        }
        return;
      }
      const emailNorm = email.trim().toLowerCase();
      const referralCode = getReferralCookie()?.trim() || undefined;
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.LOGIN_WITH_OTP}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: emailNorm,
            otp: otpValue,
            ...(referralCode && { referral_code: referralCode }),
          }),
        },
      );
      const data: SignInResponse = await response.json();
      if (!response.ok)
        throw new Error(
          (data as any).detail || (data as any).message || "Sign in failed",
        );
      if (data.userData?.apiToken) setAuthToken(data.userData.apiToken);
      // Flag new users so dashboard shows the name collection popup
      if (payloadIsNewUser(data)) {
        sessionStorage.setItem('show_name_onboarding', 'true');
        await signUpSuccessful({
          method: "email_otp",
          login_entry: loginEntry,
        });
        trackSignup();
      }
      await loginSuccess({ method: "email_otp", login_entry: loginEntry });
      trackLogin();
      if (data.shouldPromptForReferral && !getReferralCookie()?.trim()) {
        setPendingRedirect({ skillAssessment: data.skillAssessmentRedirect });
        setPendingApiToken(data.userData.apiToken);
        setShowReferralModal(true);
        return;
      }
      if (!data.shouldPromptForReferral) {
        clearReferralCookie();
      }
      window.location.replace(
        await resolvePostLoginRedirectOrDashboardAsync({
          skillAssessmentRedirect: data.skillAssessmentRedirect,
        }),
      );
    } catch (err) {
      const raw = err instanceof Error ? err.message : "An error occurred";
      setError(mapRecruiterOnlyError(raw));
      loginFailure({
        method: "email_otp",
        login_entry: loginEntry,
        failure_step: "verify_otp",
        error_message: err instanceof Error ? err.message : "Unknown",
      });
      if (signupStarted) {
        await signUpFailure({
          method: "email_otp",
          login_entry: loginEntry,
          failure_step: "verify_otp",
          error_message: err instanceof Error ? err.message : "Unknown",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (
    index: number,
    value: string,
    currentInput: HTMLInputElement | null,
  ) => {
    if (value !== "" && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value !== "" && index < 5) {
      setTimeout(() => {
        const next = currentInput?.nextElementSibling;
        if (next instanceof HTMLInputElement) next.focus();
      }, 0);
    }
  };

  const handleOtpPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const { next, focusIndex } = applyOtpPaste(
      otp,
      e.clipboardData.getData("text"),
      index,
    );
    setOtp(next);
    setTimeout(() => {
      otpInputRefs.current[focusIndex]?.focus();
    }, 0);
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
    currentInput: HTMLInputElement | null,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      e.preventDefault();
      const prev = currentInput?.previousElementSibling;
      if (prev instanceof HTMLInputElement) prev.focus();
    } else if (e.key === "Backspace" && otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      setTimeout(() => {
        const prev = currentInput?.previousElementSibling;
        if (prev instanceof HTMLInputElement) prev.focus();
      }, 0);
    }
  };

  const handleResendOtp = async () => {
    if (!email?.trim()) {
      setError("Please enter your email");
      return;
    }
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setError(emailValidationError);
      return;
    }
    setError("");
    setIsResendingOtp(true);
    try {
      const otpData = await sendOtpApi(email.trim().toLowerCase(), "login");
      setOtp(["", "", "", "", "", ""]);
      setOtpDeadlineMsState(otpDeadlineMs(otpData?.expires_at));
    } catch (err) {
      const raw = err instanceof Error ? err.message : "An error occurred";
      setError(mapRecruiterOnlyError(raw));
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleChangeEmail = () => {
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpDeadlineMsState(null);
    setError("");
    setSignupStarted(false);
  };

  const completeGoogleLogin = async (
    idToken: string,
    referralCode?: string,
  ) => {
    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.GOOGLE_LOGIN}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idToken,
          ...(referralCode && { referral_code: referralCode }),
        }),
      },
    );
    const data: GoogleLoginResponse = await response.json();
    if (!response.ok)
      throw new Error(
        (data as any).detail || (data as any).message || "Google login failed",
      );
    if (data.userData?.apiToken) setAuthToken(data.userData.apiToken);
    if (payloadIsNewUser(data)) {
      await signUpSuccessful({ method: "google", login_entry: loginEntry });
      trackSignup();
    }
    await loginSuccess({ method: "google", login_entry: loginEntry });
    trackLogin();
    if (data.shouldPromptForReferral && !getReferralCookie()?.trim()) {
      setPendingRedirect({ skillAssessment: data.skillAssessmentRedirect });
      setPendingApiToken(data.userData.apiToken);
      setShowReferralModal(true);
      return;
    }
    if (!data.shouldPromptForReferral) clearReferralCookie();
    window.location.replace(
      await resolvePostLoginRedirectOrDashboardAsync({
        skillAssessmentRedirect: data.skillAssessmentRedirect,
      }),
    );
  };

  const handleGoogleLogin = async () => {
    setError("");
    loginAttempt({ method: "google", login_entry: loginEntry });
    try {
      const idToken = await triggerGoogleLogin();
      if (!idToken) {
        if (googleError) setError(googleError);
        return;
      }
      const referralCode = getReferralCookie()?.trim() || undefined;
      await completeGoogleLogin(idToken, referralCode);
    } catch (err) {
      const raw =
        err instanceof Error
          ? err.message
          : "An error occurred during Google login";
      setError(mapRecruiterOnlyError(raw));
      loginFailure({
        method: "google",
        login_entry: loginEntry,
        failure_step: "google",
        error_message: err instanceof Error ? err.message : "Unknown",
      });
    }
  };

  const handleReferralContinue = async (referralCode: string) => {
    if (!pendingApiToken || !pendingRedirect) return;
    setShowReferralModal(false);
    try {
      await submitReferralCode(referralCode, pendingApiToken);
    } catch (e) {
      console.error("Referral submit error:", e);
    }
    window.location.replace(
      await resolvePostLoginRedirectOrDashboardAsync({
        skillAssessmentRedirect: pendingRedirect.skillAssessment,
      }),
    );
    setPendingApiToken(null);
    setPendingRedirect(null);
  };

  const handleReferralProceedWithout = async () => {
    if (!pendingRedirect) return;
    setShowReferralModal(false);
    window.location.replace(
      await resolvePostLoginRedirectOrDashboardAsync({
        skillAssessmentRedirect: pendingRedirect.skillAssessment,
      }),
    );
    setPendingApiToken(null);
    setPendingRedirect(null);
  };

  if (!open) return null;

  // Referral step replaces login/OTP UI (avoids z-index stack: login overlay is 9999 vs referral 1000)
  if (showReferralModal) {
    return (
      <ReferralCodeModal
        isOpen
        onContinue={handleReferralContinue}
        onProceedWithout={handleReferralProceedWithout}
        isSubmitting={false}
      />
    );
  }

  return (
      <div
        className={styles.overlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        onClick={required ? undefined : onClose}
      >
        <div
          className={`${styles.card} ${otpSent ? styles.cardOtp : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.cardInner}>
            <Image src={asset("/assets/Icons/meritlens-logo-light-medium.png")} alt="meritlens" width={280} height={90} style={{ height: '60px', width: 'auto' }} />

            <div className={styles.tabPanelSwitchWrap}>
              <div
                role="tabpanel"
                id="login-panel-personal"
                aria-label="Sign in to meritlens"
                className={styles.tabPanel}
              >
                  {!otpSent && (
                    <>
                      <h2 id="login-modal-title" className={styles.headline}>
                        Start Your Interview Practice
                      </h2>
                      <p className={styles.subtext}>
                        Sign in to save your progress and apply in seconds.
                      </p>
                    </>
                  )}

                  <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                      <div className={styles.errorMessage}>{error}</div>
                    )}

                    {otpSent ? (
                      <div className={styles.otpSection}>
                        <h2 id="login-modal-title" className={styles.otpTitle}>
                          Verify your email
                        </h2>
                        <p className={styles.otpSubtitle}>
                          We&apos;ve sent a 6-digit code to your email:{" "}
                          <span className={styles.otpEmailInline}>{email}</span>
                        </p>
                        <div className={styles.otpInputGroup}>
                          <label
                            className={styles.otpCodeLabel}
                            htmlFor="login-modal-otp-0"
                          >
                            Enter verification code
                          </label>
                          <div className={styles.otpInputContainer}>
                            {otp.map((digit, i) => (
                              <input
                                key={i}
                                id={i === 0 ? "login-modal-otp-0" : undefined}
                                ref={(el) => {
                                  otpInputRefs.current[i] = el;
                                }}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                placeholder="-"
                                onChange={(e) =>
                                  handleOtpChange(
                                    i,
                                    e.target.value.slice(-1),
                                    e.currentTarget,
                                  )
                                }
                                onKeyDown={(e) =>
                                  handleOtpKeyDown(i, e, e.currentTarget)
                                }
                                onPaste={(e) => handleOtpPaste(i, e)}
                                className={`${styles.otpInput} ${digit ? styles.otpInputFilled : ""}`}
                                autoFocus={i === 0}
                                aria-label={`Digit ${i + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                        {resendCountdown > 0 ? (
                          <div className={styles.otpTimer}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="9" stroke="#5A7184" strokeWidth="2"/>
                              <path d="M12 7V12L15 15" stroke="#5A7184" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Resend in</span>
                            <span className={styles.otpTimerCount}>{resendCountdown}s</span>
                          </div>
                        ) : (
                          <div className={styles.otpResend}>
                            <span>Didn&apos;t receive the code?</span>
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              className={styles.linkButton}
                              disabled={isLoading || isResendingOtp}
                            >
                              {isResendingOtp ? "Sending..." : "Resend OTP"}
                            </button>
                          </div>
                        )}
                        <button
                          type="submit"
                          className={styles.primaryBtn}
                          disabled={
                            isLoading ||
                            isResendingOtp ||
                            resendCountdown <= 0 ||
                            otp.join("").length !== 6
                          }
                        >
                          {isLoading ? "Signing in..." : "Sign In"}
                        </button>
                        <button
                          type="button"
                          onClick={handleChangeEmail}
                          className={styles.otpCancelLink}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.googleBtn}
                          onClick={handleGoogleLogin}
                          disabled={isGoogleLoading}
                        >
                          <svg
                            className={styles.googleLogo}
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              fill="#4285F4"
                            />
                            <path
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              fill="#34A853"
                            />
                            <path
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              fill="#FBBC05"
                            />
                            <path
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              fill="#EA4335"
                            />
                          </svg>
                          Login with Google
                        </button>
                        <button
                          type="button"
                          className={styles.githubBtn}
                          onClick={() => triggerGithubLogin()}
                          disabled={isGithubLoading}
                        >
                          <svg
                            className={styles.githubLogo}
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#1f2937"
                          >
                            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
                          </svg>
                          Login with GitHub
                        </button>
                        <div className={styles.divider}>or</div>
                        <div className={styles.inputWrap}>
                          <span className={styles.inputIcon} aria-hidden>
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          </span>
                          <input
                            id="login-modal-email"
                            type="email"
                            className={styles.input}
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                          />
                        </div>
                        <button
                          type="submit"
                          className={styles.primaryBtn}
                          disabled={isLoading || !email?.trim()}
                        >
                          {isLoading ? "Sending code..." : "Login and Continue"}
                        </button>
                      </>
                    )}
                  </form>


                </div>
            </div>
          </div>
        </div>
      </div>
  );
}
